import MyEvolutionsActionModal, {
  MyEvolutionsActionModalState,
} from "@/components/molecules/ActionModals/MyEvolutionsActionModal/MyEvolutionsActionModal";
import { getVersionAssetsSelector } from "@/redux/loadedContent/loadedContentSlice";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useNavigate } from "react-router-dom";

import { APIStatus } from "../../../api/APIStatus.type";
import { getMyEvolutionPlayersStatus } from "../../../api/privateRequests/players/getMyEvolutionPlayersStatus";
import { fireGTMEvent } from "../../../functions/fireGTMEvent";
import { lock as lockIcon } from "../../../icons";
import {
  MetaRatingsPlayer,
  generateNavigationUrl,
  updateMyEvolvedPlayersStatus,
} from "../../../redux/players/playersSlice";
import { AppDispatch } from "../../../redux/store";
import { Button } from "../../atoms/Buttons/Button";
import Spinner from "../../atoms/Spinner/Spinner";
import { InfoIcon } from "../../features/MyEvolutions/InfoIcon";
import { LoadingEvolutionPaths } from "../../features/MyEvolutions/LoadingEvolutionPaths";
import {
  SquadBuilderPlayer,
  SquadBuilderPlayerProps,
} from "../../molecules/Player/SquadBuilderPlayer/SquadBuilderPlayer";

type Props = {
  players: MetaRatingsPlayer[];
  status: APIStatus;
  canImport: boolean;
  isMobile: boolean;
  onHandleImportClicked?: () => void;
};

export const MyEvolvedPlayersList = ({
  players,
  status,
  canImport,
  isMobile,
  onHandleImportClicked,
}: Props) => {
  const assetsBaseUrl = import.meta.env.VITE_APP_ASSETS_BASE_URL;
  const versionAssets = useSelector(getVersionAssetsSelector);
  const loadingPlayers = players.filter((p) => p.status === "LOADING");
  const dispatch = useDispatch<AppDispatch>();
  const [showActionModal, setShowActionModal] = useState<
    undefined | MyEvolutionsActionModalState
  >();
  const [chosenPlayer, setChosenPlayer] = useState<SquadBuilderPlayerProps>();
  const navigate = useNavigate();

  const getPlayerStatusInfo = (
    player: MetaRatingsPlayer,
    setShowActionModal: (state: "needs-report" | "needs-confirmation") => void,
  ) => {
    let buttonText: string;
    let labelText: string;
    let icon: React.ReactNode;
    let disabled = false;
    let onClickActionButton: (e: React.MouseEvent<HTMLButtonElement>) => void;

    if (player.status === "IN_PROGRESS") {
      buttonText = "In-Progress";
      disabled = true;
    } else if (player.status === "NEEDS_REPORT") {
      buttonText = "Send Report";
      labelText = "Unable to detect path";
      icon = <InfoIcon type="warning" />;
      onClickActionButton = (e) => {
        e.stopPropagation();
        setShowActionModal("needs-report");
      };
    } else if (player.status === "NEEDS_CONFIRMATION") {
      buttonText = "Action Required";
      icon = <InfoIcon type="info" />;
      onClickActionButton = (e) => {
        e.stopPropagation();
        setShowActionModal("needs-confirmation");
      };
    } else if (player.status === "MATCHING_FAILED") {
      buttonText = "Matching Failed";
      disabled = true;
      icon = <InfoIcon type="warning" />;
    } else if (player.status === "MATCHED") {
      buttonText = availableEvosText(player.availableEvos);
      disabled = player.availableEvos === 0;
    } else if (player.status === "REPORT_SENT") {
      buttonText = "Report Sent";
      labelText = "Unable to detect path";
      disabled = true;
      icon = <InfoIcon type="info" />;
    }
    return { buttonText, labelText, icon, disabled, onClickActionButton };
  };
  useEffect(() => {
    const localGetMyEvolutionPlayersStatus = async (evoPathIds: number[]) => {
      const res = await getMyEvolutionPlayersStatus(evoPathIds);
      dispatch(updateMyEvolvedPlayersStatus(res));
    };
    if (loadingPlayers.length > 0) {
      const intervalId = setInterval(() => {
        localGetMyEvolutionPlayersStatus(
          loadingPlayers.map((p) => p.evoPathId),
        );
      }, 2000); // Call the function every 2000 milliseconds (2 seconds)

      // Clear the interval if loadingPlayers is empty
      return () => clearInterval(intervalId);
    }
  }, [loadingPlayers, dispatch]);

  const availableEvosText = (availableEvos: number) => {
    return availableEvos + " Evolution" + (availableEvos !== 1 ? "s" : "");
  };

  return (
    <section
      className={clsx(
        "relative min-h-128 w-full text-white mobile:px-10 p-10 px-4 rounded-b-xl mx-auto gap-12 sb-large:gap-16 gap-y-8 desktop:gap-y-12 sb-large:gap-y-24 flex flex-wrap justify-center mobile:justify-start pb-24",
        players.length === 0
          ? "bg-empty-list h-128 desktop:h-176"
          : "bg-primary-dark",
      )}
    >
      {showActionModal && (
        <MyEvolutionsActionModal
          state={showActionModal}
          player={chosenPlayer}
          onDismiss={() => {
            setChosenPlayer(undefined);
            setShowActionModal(undefined);
          }}
        />
      )}
      {status === "pending" ? (
        <div className="mx-auto ">
          <Spinner />
        </div>
      ) : (players.length === 0 && status === "fulfilled") ||
        status === "rejected" ? (
        isMobile ? (
          <div className="my-[10%] flex flex-col text-center gap-y-2">
            <h3 className="text-xl font-semibold">
              No Evolution Players Available
            </h3>
            <p className="font-medium text-gray-300 ">
              Import your players in order to see your evolved players (Desktop
              required)
            </p>
          </div>
        ) : (
          <div className="flex flex-col items-center text-2xl font-medium my-[10%] desktop:mx-auto gap-y-6">
            No Evolution Players Imported
            <Button
              onClick={onHandleImportClicked}
              size="large"
              locked={!canImport}
              icon={!canImport && lockIcon}
            >
              Import Club
            </Button>
          </div>
        )
      ) : (
        players.map((p) => {
          const { buttonText, labelText, icon, disabled, onClickActionButton } =
            getPlayerStatusInfo(p, setShowActionModal);
          const versionName = versionAssets[p.versionId]?.name || "";
          const navLink = generateNavigationUrl({
            playerRoleId: p.playerRoleId,
            evoPathId: p.evoPathId,
            name: p.name,
            versionName,
            resourceId: p.resourceId,
            canEvolve: p.canEvolve,
            evolutionPath: p.evolutionPath,
          });

          const imageId = p.hasDynamicImage ? p?.resourceId : p?.assetId;
          const playerUrl =
            p?.playerUrl ?? `${assetsBaseUrl}/fc25/players/${imageId}.png`;

          const squadBuilderPlayerProps = {
            player: {
              ...p,
              metaRatings: [],
              playerUrl,
              possiblePositions: p.positions,
              rolesPlus: [],
            },
            metaRatingZeroChem: p.metaRatingZeroChem,
            position: p.preferredPosition,
            metaRating: p.metaRating,
            chemStyleId: 0,
            playerRoleId: p.playerRoleId,
            isOnPitch: false,
            tabletOrLargerMode: !isMobile,
          };

          return (
            <article className="h-fit" key={p.evoPathId}>
              <div className="relative flex pb-24 desktop:pb-8 large-2:pb-0 flex-col items-center px-6 mobile:px-0">
                <div className={clsx(isMobile ? "scale-[190%]" : "scale-125")}>
                  <NavLink
                    state={{
                      from: "/evolutions/my-evolutions",
                      scrollPosition: window.scrollY,
                    }}
                    to={navLink}
                    key={navLink + p.evolutionPath?.join("")}
                    onClick={() => fireGTMEvent("clicked_player_to_view")}
                  >
                    <SquadBuilderPlayer
                      {...squadBuilderPlayerProps}
                      hidePrice
                    />
                  </NavLink>
                </div>
                <div className="absolute bottom-4 large-2:-bottom-12 desktop:-bottom-4 flex flex-col items-center">
                  {labelText && (
                    <span className="text-[10px] mr-2 text-white rounded-full">
                      {labelText}
                    </span>
                  )}
                  <Button
                    onClick={(e) => {
                      if (p.status === "MATCHED") {
                        navigate(navLink, {
                          state: {
                            from: "/evolutions/my-evolutions",
                            scrollPosition: window.scrollY,
                          },
                        });
                      } else {
                        e.preventDefault();
                        setChosenPlayer(squadBuilderPlayerProps);
                        onClickActionButton(e);
                      }
                    }}
                    size={"small"}
                    disabled={disabled}
                  >
                    {buttonText}
                  </Button>
                </div>
                <div
                  className="absolute bottom-12 desktop:bottom-4 large-2:-bottom-4 -right-3 large-2:-right-5"
                  onClick={(e) => e.preventDefault()}
                >
                  {icon}
                </div>
                {p.status === "LOADING" && (
                  <div
                    className={clsx(
                      isMobile ? "scale-150" : "scale-125",
                      "absolute w-full h-full bg-black/50 cursor-default flex pb-6",
                    )}
                    onClick={(e) => e.preventDefault()}
                  >
                    <div className="mx-auto my-auto">
                      <LoadingEvolutionPaths />
                    </div>
                  </div>
                )}
              </div>
            </article>
          );
        })
      )}
    </section>
  );
};
